import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Crown, Medal, Star, TrendingUp, User, ChevronRight } from 'lucide-react';
import Button from '../../../components/ui/Button';

const Leaderboard = ({ preview = false, onViewFull }) => {
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [timeframe, setTimeframe] = useState('weekly');

  useEffect(() => {
    // Mock leaderboard data
    setLeaderboardData([
      {
        rank: 1,
        username: 'TestMaster_Pro',
        points: 15420,
        challengesCompleted: 89,
        winRate: 94,
        avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face&round=full',
        level: 'Expert',
        badges: 24,
        streak: 15,
        specialBadge: 'Champion'
      },
      {
        rank: 2,
        username: 'AutomationQueen',
        points: 13890,
        challengesCompleted: 76,
        winRate: 91,
        avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=40&h=40&fit=crop&crop=face&round=full',
        level: 'Expert',
        badges: 19,
        streak: 12,
        specialBadge: 'API Expert'
      },
      {
        rank: 3,
        username: 'BugHunter_Elite',
        points: 12650,
        challengesCompleted: 68,
        winRate: 89,
        avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop&crop=face&round=full',
        level: 'Advanced',
        badges: 16,
        streak: 8,
        specialBadge: 'Bug Hunter'
      },
      {
        rank: 4,
        username: 'SeleniumNinja',
        points: 11280,
        challengesCompleted: 62,
        winRate: 87,
        avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=40&h=40&fit=crop&crop=face&round=full',
        level: 'Advanced',
        badges: 14,
        streak: 6,
        specialBadge: 'UI Master'
      },
      {
        rank: 5,
        username: 'PerformanceGuru',
        points: 10950,
        challengesCompleted: 58,
        winRate: 85,
        avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=40&h=40&fit=crop&crop=face&round=full',
        level: 'Advanced',
        badges: 13,
        streak: 4,
        specialBadge: 'Performance Pro'
      },
      {
        rank: 6,
        username: 'SecuritySage',
        points: 9870,
        challengesCompleted: 51,
        winRate: 82,
        avatar: 'https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?w=40&h=40&fit=crop&crop=face&round=full',
        level: 'Intermediate',
        badges: 11,
        streak: 3,
        specialBadge: 'Security Expert'
      }
    ]);
  }, [timeframe]);

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1: return <Crown className="w-5 h-5 text-yellow-500" />;
      case 2: return <Medal className="w-5 h-5 text-gray-400" />;
      case 3: return <Medal className="w-5 h-5 text-amber-600" />;
      default: return <span className="w-5 h-5 flex items-center justify-center text-sm font-bold">#{rank}</span>;
    }
  };

  const getRankBgColor = (rank) => {
    switch (rank) {
      case 1: return 'bg-gradient-to-r from-yellow-100 to-yellow-200 border-yellow-300';
      case 2: return 'bg-gradient-to-r from-gray-100 to-gray-200 border-gray-300';
      case 3: return 'bg-gradient-to-r from-amber-100 to-amber-200 border-amber-300';
      default: return 'bg-background border-border hover:bg-muted/50';
    }
  };

  const displayData = preview ? leaderboardData?.slice(0, 5) : leaderboardData;

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className="bg-card border border-border rounded-lg p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Trophy className="w-5 h-5 text-yellow-500" />
          <h3 className="font-semibold">
            {preview ? 'Top Performers' : 'Leaderboard'}
          </h3>
        </div>
        
        {!preview && (
          <div className="flex items-center space-x-2">
            <Button
              variant={timeframe === 'weekly' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setTimeframe('weekly')}
            >
              Weekly
            </Button>
            <Button
              variant={timeframe === 'monthly' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setTimeframe('monthly')}
            >
              Monthly
            </Button>
            <Button
              variant={timeframe === 'allTime' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setTimeframe('allTime')}
            >
              All Time
            </Button>
          </div>
        )}
      </div>
      <div className="space-y-3">
        {displayData?.map((user, index) => (
          <motion.div
            key={user?.username}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`p-3 rounded-lg border transition-colors ${getRankBgColor(user?.rank)}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-8 h-8">
                  {getRankIcon(user?.rank)}
                </div>
                
                <img
                  src={user?.avatar || `https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face`}
                  alt={user?.username}
                  className="w-8 h-8 rounded-full object-cover"
                />
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium truncate">{user?.username}</span>
                    {user?.specialBadge && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                        {user?.specialBadge}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-3 text-xs text-muted-foreground">
                    <span>{user?.level}</span>
                    <span>•</span>
                    <span>{user?.challengesCompleted} completed</span>
                    <span>•</span>
                    <span>{user?.winRate}% success</span>
                  </div>
                </div>
              </div>

              <div className="text-right">
                <div className="font-bold text-lg">
                  {user?.points?.toLocaleString()}
                </div>
                <div className="text-xs text-muted-foreground">points</div>
              </div>
            </div>

            {/* Additional stats for top 3 */}
            {user?.rank <= 3 && !preview && (
              <div className="mt-3 pt-3 border-t border-border/50">
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center space-x-2">
                    <Star className="w-3 h-3 text-yellow-500" />
                    <span>{user?.badges} badges</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <TrendingUp className="w-3 h-3 text-green-500" />
                    <span>{user?.streak} day streak</span>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </div>
      {preview && (
        <div className="mt-4 pt-4 border-t border-border">
          <Button
            variant="ghost"
            size="sm"
            onClick={onViewFull}
            className="w-full flex items-center justify-center space-x-2"
          >
            <span>View Full Leaderboard</span>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
      {!preview && (
        <div className="mt-6 pt-4 border-t border-border text-center">
          <div className="bg-muted/50 rounded-lg p-3 mb-3">
            <div className="text-sm text-muted-foreground mb-1">Your Rank</div>
            <div className="flex items-center justify-center space-x-2">
              <User className="w-4 h-4" />
              <span className="font-semibold">#47</span>
              <span className="text-muted-foreground">•</span>
              <span className="font-medium">2,840 points</span>
            </div>
          </div>
          <Button size="sm" variant="outline">
            View Your Profile
          </Button>
        </div>
      )}
    </motion.div>
  );
};

export default Leaderboard;